import axios from 'axios';
import cache from '../helpers/redis';
import errorRedirectHandler from '../helpers/errorRedirectHandler';

import { User } from '../types/api/UsersTypes';
import { SessionState } from '../types/state/reducers/authReducersTypes';
import { QueryParams } from '../types/api/BasicTypes';
import { GetServerSidePropsContext } from 'next';
import { PagedResponse } from '../types/api/Http';
import { CollectionResponse } from '../types/api/CollectionsIndexTypes';
import { CategoryResponse } from '../types/api/CategoriesIndexTypes';
import { CollectionOwnershipView } from '../types/api/OwnershipTypes';
import { ProductPageServerSideProps } from '../types/pages/products/productsPageTypes';
import { RedirectConfigProps } from '../types/pages/generic/redirectConfig';
import { ServerSideProps } from '../types/pages/generic/serverSideProps';

import { ENV } from '../constants/environments';
import { LIBRARY_IDS, CACHE_KEYS } from '../constants/api';
const { LIBRARY_ID_2D, LIBRARY_ID_3D } = LIBRARY_IDS;
const {
  CATEGORIES_CACHE_KEY_2D,
  CATEGORIES_CACHE_KEY_3D,
  COLLECTIONS_CACHE_KEY_2D,
  COLLECTIONS_CACHE_KEY_3D
} = CACHE_KEYS;

export function productIsPFL(product: { sku: string }): boolean {
  const regExp = new RegExp('PF[0-9]+-?[0-9]+');
  return regExp.test(product.sku);
}

export function productIs3D(product: { sku: string }): boolean {
  const regExp = new RegExp('3D.*');
  return regExp.test(product.sku);
}

const apiUrl: string = `${ENV.api.baseURL}`;
const collectionUrl = `${apiUrl}/collections`;
const searchUrl = `${apiUrl}/products/search`;

const fetchCollectionsFromBackend = async (
  collectionParams: QueryParams,
  user: User,
): Promise<PagedResponse<CollectionResponse>> => {
  let collectionsUrl: string =
    (collectionParams.search as string)?.length > 0 ? searchUrl : collectionUrl;
  // temporarily use both API versions for benchmarking & performance testing
  if (process.env.USE_V2_PRODUCT_ENDPOINTS === 'true') {
    collectionsUrl = collectionUrl.replace('v1', 'v2');
    if (collectionParams && collectionParams.search) {
      collectionParams.search_query = collectionParams.search;
      delete collectionParams.search;
      collectionParams.search_query = `‘${collectionParams.search_query}’`;
    }
  };
  const collectionsRes = await axios.get(collectionsUrl, {
    params: collectionParams,
    headers: {
      authorization: user?.token
    }
  });
  return collectionsRes?.data;
};

const getCollections = async (
  collectionParams: QueryParams,
  session: SessionState,
  library_id: number
): Promise<PagedResponse<CollectionResponse>> => {
  const collectionsUrl: string =
    (collectionParams.search as string)?.length > 0 ? searchUrl : collectionUrl;
  let key: string;
  switch (library_id) {
    case LIBRARY_ID_3D:
      key = COLLECTIONS_CACHE_KEY_3D;
      break;
    case LIBRARY_ID_2D:
    default:
      key = COLLECTIONS_CACHE_KEY_2D;
      break;
  }
  const cacheKey = JSON.stringify({
    user: session?.user?.id,
    url: collectionsUrl,
    params: collectionParams,
    key
  });
  const { fetchCollectionsFromBackend, cache } = productsPageHelpers;
  return await cache(cacheKey, 3600, async () =>
    fetchCollectionsFromBackend(collectionParams, session.user as User)
  );
};

const fetchCategoriesFromBackend = async (library_id: number): Promise<Array<CategoryResponse>> => {
  const categoriesUrl: string = `${apiUrl}/categories?limit=50&library_id=${library_id}`;
  const categoriesRes = await axios.get(categoriesUrl);
  return categoriesRes?.data?.data;
};

const getCategories = async (library_id: number): Promise<Array<CategoryResponse>> => {
  let key: string;
  switch (library_id) {
    case LIBRARY_ID_3D:
      key = CATEGORIES_CACHE_KEY_3D;
      break;
    case LIBRARY_ID_2D:
    default:
      key = CATEGORIES_CACHE_KEY_2D;
      break;
  }
  const { fetchCategoriesFromBackend, cache } = productsPageHelpers;
  return await cache(key, 3600, async () => fetchCategoriesFromBackend(library_id));
};

const fetchOwnershipFromBackend = async (user: User): Promise<Array<CollectionOwnershipView>> => {
  const ownershipUrl: string = `${apiUrl}/collections/ownership`;
  let ownershipRes: any;
  ownershipRes = await axios.get(ownershipUrl, {
    headers: {
      authorization: user?.token
    }
  });
  return ownershipRes?.data?.data;
};

const getOwnerships = async (user: User): Promise<Array<CollectionOwnershipView>> => {
  const { fetchOwnershipFromBackend, cache } = productsPageHelpers;
  return await cache(`avfx_ownership_${user.id}`, 600, async () => fetchOwnershipFromBackend(user));
};

const fetchProductsServersideProps = async (
  ctx: GetServerSidePropsContext,
  session: SessionState,
  library_id: number
): Promise<ServerSideProps<ProductPageServerSideProps> | RedirectConfigProps> => {
  const { fetchCollectionsFromBackend, getCategories, getOwnerships } = productsPageHelpers;

  let collectionParams: QueryParams = {
    desc: false,
    library_id,
    ...ctx.query
  };

  let categories: Array<CategoryResponse>;
  let collectionsRes: PagedResponse<CollectionResponse>;
  let ownership: { [key: string]: CollectionOwnershipView };
  let promisesError: any;

  const getCategoriesPromise = getCategories(library_id);
  const getCollectionsPromise = fetchCollectionsFromBackend(collectionParams, session?.user as User);
  const promises: Array<Promise<any>> = [getCategoriesPromise, getCollectionsPromise];
  if (library_id === LIBRARY_ID_3D && session?.user?.id) {
    const getOwnershipsPromise = getOwnerships(session?.user as User);
    promises.push(getOwnershipsPromise);
  }

  await Promise.all(promises)
    .then((response: Array<any>) => {
      categories = response[0];
      collectionsRes = response[1];
      if (response.length < 3) return;
      ownership = response[2];
    })
    .catch((error) => {
      promisesError = error;
    });

  if (promisesError) {
    console.log(promisesError)
    return errorRedirectHandler(promisesError);
  }

  let searchResultsCount = collectionsRes.total_count;

  // If search miss - let's show most popular collections
  if ((collectionParams?.search as string)?.length > 0 && collectionsRes.total_count == 0) {
    try {
      collectionParams = {
        ...collectionParams,
        sort_by: 'most_popular',
        search: null
      };
      collectionsRes = await fetchCollectionsFromBackend(collectionParams, session.user as User);
      searchResultsCount = 0;
    } catch (error) {
      console.log(error)
      return errorRedirectHandler(error);
    }
  }

  const props: ProductPageServerSideProps = {
    categories: categories || [],
    collections: collectionsRes?.data,
    totalCollections: collectionsRes?.total_count,
    totalPages: collectionsRes?.total_pages,
    currentPage: collectionsRes?.page,
    collectionParams: collectionParams,
    searchResultsCount: searchResultsCount,
    collectionOwnership: ownership || null,
    query_id: collectionsRes?.query_id || '',
  };

  return { props };
};

const productsPageHelpers = {
  fetchProductsServersideProps,
  getCollections,
  getCategories,
  getOwnerships,
  productIsPFL,
  productIs3D,
  fetchCollectionsFromBackend,
  fetchCategoriesFromBackend,
  fetchOwnershipFromBackend,
  cache
};

export default productsPageHelpers;
